import {
  scheduleTagKeyMaxLength,
  scheduleTagMaxPairs,
  scheduleTagValueMaxLength,
  type IScheduleTagPair,
  type ScheduleTags,
} from '@cortex/types';
import type { CSSProperties } from 'react';

/**
 * Site / customer tags on a schedule (DEV-17502): the chips every list shows
 * and the key / value editor the Schedule dialog embeds. Both read and write
 * the flat `{ key: value }` map the api stores; the editor keeps rows so a
 * person can type a key before its value.
 */

const chipRowStyle: CSSProperties = {
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-1)',
  alignItems: 'center',
};

/** The id of the key suggestions list the editor's key inputs point at. */
const keySuggestionsListId = 'schedule-tag-key-suggestions';

/** `key: value` chips, or a muted "Org-wide" when the schedule has none. */
export function ScheduleTagChips({ tags }: { tags: ScheduleTags | null | undefined }) {
  const pairs = Object.entries(tags ?? {});
  if (pairs.length === 0) {
    return (
      <span className="t-muted t-xs" data-testid="schedule-tags-none">
        Org-wide
      </span>
    );
  }
  return (
    <span style={chipRowStyle} data-testid="schedule-tags">
      {pairs.map(([key, value]) => (
        <span
          key={key}
          className="badge badge--outline t-mono"
          title={`Schedule tag ${key}: ${value}`}
          data-testid="schedule-tag-chip"
        >
          {key}: {value}
        </span>
      ))}
    </span>
  );
}

/** Editor rows into the map the api takes. Later rows win a duplicate key; blank rows are dropped. */
export function tagRowsToTags(rows: readonly IScheduleTagPair[]): ScheduleTags {
  const out: ScheduleTags = {};
  for (const row of rows) {
    const key = row.key.trim().toLowerCase();
    const value = row.value.trim();
    if (key && value) out[key] = value;
  }
  return out;
}

/** The stored map as editor rows. */
export function tagsToTagRows(tags: ScheduleTags | null | undefined): IScheduleTagPair[] {
  return Object.entries(tags ?? {}).map(([key, value]) => ({ key, value }));
}

/**
 * Key / value rows with add and remove. `suggestions` are the keys the org
 * already uses (offered through a datalist; free text is still fine). Caps
 * the row count at the api's limit.
 */
export function ScheduleTagsEditor({
  rows,
  onChange,
  suggestions = [],
  disabled = false,
}: {
  rows: IScheduleTagPair[];
  onChange: (rows: IScheduleTagPair[]) => void;
  suggestions?: readonly string[];
  disabled?: boolean;
}) {
  const update = (index: number, patch: Partial<IScheduleTagPair>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const remove = (index: number) => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, { key: '', value: '' }]);
  const atCap = rows.length >= scheduleTagMaxPairs;

  return (
    <div className="field" data-testid="schedule-tags-editor">
      <span className="field-label">Tags (optional)</span>
      <span className="field-help">
        Site or customer this schedule is for, e.g. <code>site</code> = <code>sf-office</code>.
        Every run gets them in its input as <code>scheduleTags</code> and as top-level keys, so the
        workflow can scope what it looks at. Leave empty for an org-wide schedule.
      </span>
      {suggestions.length > 0 ? (
        <datalist id={keySuggestionsListId}>
          {suggestions.map((key) => (
            <option key={key} value={key} />
          ))}
        </datalist>
      ) : null}
      {rows.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          {rows.map((row, index) => (
            <div
              // Index keys: rows have no identity of their own, and a row being
              // typed into has no stable key yet.
              key={index}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr) auto',
                gap: 'var(--sp-1)',
                alignItems: 'center',
              }}
              data-testid="schedule-tag-row"
            >
              <input
                type="text"
                className="input"
                value={row.key}
                onChange={(e) => update(index, { key: e.target.value })}
                placeholder="key (site, customer)"
                aria-label={`Tag ${index + 1} key`}
                list={suggestions.length > 0 ? keySuggestionsListId : undefined}
                maxLength={scheduleTagKeyMaxLength}
                autoCapitalize="none"
                spellCheck={false}
                disabled={disabled}
              />
              <input
                type="text"
                className="input"
                value={row.value}
                onChange={(e) => update(index, { value: e.target.value })}
                placeholder="value"
                aria-label={`Tag ${index + 1} value`}
                maxLength={scheduleTagValueMaxLength}
                spellCheck={false}
                disabled={disabled}
              />
              <button
                type="button"
                className="btn btn--ghost btn--xs"
                onClick={() => remove(index)}
                aria-label={`Remove tag ${index + 1}`}
                disabled={disabled}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div>
        <button
          type="button"
          className="btn btn--outline btn--xs"
          onClick={add}
          disabled={disabled || atCap}
          title={atCap ? `At most ${scheduleTagMaxPairs} tags` : undefined}
        >
          Add tag
        </button>
      </div>
    </div>
  );
}
